import { useMyPortfolioCapsulesConfigSlice } from 'widgets/myPortfolioCapsules/store/config/configSlice';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useCallback, useContext, useEffect } from 'react';
import {
    computeMyPortfolioCapsulesData,
    MyPortfolioCapsulesDataItem,
} from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesData';
import { useSelectMyPortfolioCapsulesDataState } from 'widgets/myPortfolioCapsules/store/data/selectors';
import AppContext from 'app/AppContext';
import { useThrottledDataCalculation } from '@instinet-analytics/core-lib';
import { useLogger } from 'logging/writers/hooks';

const ThrottleSecs = 30;

export function useMyPortfolioCapsuleData(): MyPortfolioCapsulesDataItem[] {
    const { widgetKey } = useWidgetContext();
    const { config } = useMyPortfolioCapsulesConfigSlice();
    const {
        currencyFormat: { exchangeRate },
    } = useContext(AppContext);
    const logger = useLogger('useMyPortfolioCapsuleData');

    const aggregatedData = useSelectMyPortfolioCapsulesDataState();

    const deriveData = useCallback(() => {
        if (aggregatedData[config.aggregationKey].totals.count === 0) {
            return [];
        }

        return computeMyPortfolioCapsulesData(
            aggregatedData,
            config.aggregationKey,
            config,
            exchangeRate ?? 1
        );
    }, [aggregatedData, config, exchangeRate]);

    const { data, calculate } = useThrottledDataCalculation(
        `useMyPortfolioCapsuleData-${widgetKey}`,
        [],
        deriveData,
        logger,
        ThrottleSecs
    );

    useEffect(() => {
        // When the config changes we immediately compute...
        calculate('Config change');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);

    useEffect(() => {
        if (aggregatedData.reset) {
            calculate('Aggregated data reset');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aggregatedData]);

    return data;
}
